import { useMutation } from '@tanstack/react-query';
import { Check, Copy, FlaskConical, LogIn, Mail, Trash2, UserPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import type {
  InvitationBody,
  InviteInputBodyRole,
  MemberBody,
  SiteBody,
} from '@/shared/api/generated/model';
import {
  usersChangeRole,
  usersInvite,
  usersRemoveMember,
  usersRevokeInvitation,
} from '@/shared/api/generated/users/users';
import { ApiError } from '@/shared/api/http';
import { t } from '@/shared/i18n';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Select, SelectItem } from '@/shared/ui/Select';
import { Status } from '@/shared/ui/Status';
import { toast } from '@/shared/ui/Toast';

import styles from './MembersPanel.module.scss';

type Role = InviteInputBodyRole;

/** Роли в порядке старшинства — так их и показываем в выборе. */
const ROLES: Role[] = ['admin', 'developer', 'editor', 'author', 'viewer', 'support'];

const STATUS_TONE = { active: 'success', invited: 'warning', blocked: 'danger' } as const;

/**
 * Где показана панель: люди всего тенанта (владельцу платформы, с выбором
 * сайта) или команда одного сайта.
 */
export type PanelScope =
  | { kind: 'tenant'; tenantID: string; sites: SiteBody[] }
  | { kind: 'site'; tenantID: string; siteID: string };

export interface MembersPanelProps {
  scope: PanelScope;
  members: MemberBody[];
  invitations: InvitationBody[];
  loading: boolean;
  onChanged: () => void;
  /** Вход от имени — только владельцу платформы. */
  onEnterAs?: ((member: MemberBody) => void) | undefined;
}

/**
 * Люди и их роли: приглашение, смена роли и ограничения, снятие.
 *
 * Роль — что человек может, охват — весь тенант или один сайт, «только
 * Development» — отдельная галочка (docs/research/03-roles.md). Права
 * проверяет сервер: администратор сайта распоряжается только людьми своего
 * сайта, и если он попробует выдать роль на весь тенант, отказ придёт оттуда
 * и будет показан у формы.
 */
export function MembersPanel({
  scope,
  members,
  invitations,
  loading,
  onChanged,
  onEnterAs,
}: MembersPanelProps) {
  const [link, setLink] = useState<string>();

  const act = (run: Promise<unknown>, done: string) => {
    run
      .then(() => {
        toast.success(done);
        onChanged();
      })
      .catch((cause: unknown) => {
        toast.error(t.team.failed, { description: describe(cause) });
      });
  };

  return (
    <div className={styles['panel']}>
      <InviteForm
        scope={scope}
        onInvited={(token) => {
          setLink(`${window.location.origin}/invite/${token}`);
          onChanged();
        }}
      />

      {link !== undefined && <InviteLink link={link} />}

      {loading && <p className={styles['muted']}>{t.team.loading}</p>}

      {invitations.length > 0 && (
        <section className={styles['group']}>
          <h3 className={styles['group__title']}>{t.team.invitations}</h3>

          {invitations.map((invitation) => (
            <div key={invitation.id} className={styles['row']}>
              <Mail size={15} className={styles['row__icon']} />
              <span className={styles['row__main']}>{invitation.email}</span>
              <GrantBadges
                role={invitation.role}
                devOnly={invitation.dev_only}
                siteName={invitation.site_name}
              />
              <span className={styles['spacer']} />
              <Button
                iconOnly
                variant="ghost"
                tone="danger"
                size="sm"
                aria-label={t.team.revoke(invitation.email)}
                onClick={() => {
                  act(usersRevokeInvitation(invitation.id), t.team.revoked);
                }}
              >
                <Trash2 size={15} />
              </Button>
            </div>
          ))}
        </section>
      )}

      <section className={styles['group']}>
        <h3 className={styles['group__title']}>{t.team.people}</h3>

        {members.length === 0 && !loading && <p className={styles['muted']}>{t.team.nobody}</p>}

        {members.map((member) => (
          <div key={member.membership_id} className={styles['row']}>
            <span className={styles['row__main']}>
              <span className={styles['row__title']}>
                {member.name === '' ? member.email : member.name}
              </span>
              <span className={styles['row__sub']}>{member.email}</span>
            </span>

            <Status tone={STATUS_TONE[member.status]}>{t.team.statuses[member.status]}</Status>
            <GrantBadges role={member.role} devOnly={member.dev_only} siteName={member.site_name} />

            <span className={styles['spacer']} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" tone="neutral" size="sm">
                  {t.team.actions}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t.team.changeRole}</DropdownMenuLabel>

                {ROLES.map((role) => (
                  <DropdownMenuItem
                    key={role}
                    disabled={role === member.role}
                    onSelect={() => {
                      // У администратора ограничения не бывает — при
                      // повышении до администратора оно снимается.
                      const devOnly = role === 'admin' ? false : member.dev_only;
                      act(
                        usersChangeRole(
                          member.membership_id,
                          { role, dev_only: devOnly },
                          { 'If-Match': member.etag },
                        ),
                        t.team.roleChanged,
                      );
                    }}
                  >
                    {t.team.roles[role]}
                  </DropdownMenuItem>
                ))}

                {member.role !== 'admin' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      icon={<FlaskConical size={15} />}
                      onSelect={() => {
                        act(
                          usersChangeRole(
                            member.membership_id,
                            {
                              role: member.role,
                              dev_only: !member.dev_only,
                            },
                            { 'If-Match': member.etag },
                          ),
                          t.team.roleChanged,
                        );
                      }}
                    >
                      {member.dev_only ? t.team.unsetDevOnly : t.team.setDevOnly}
                    </DropdownMenuItem>
                  </>
                )}

                {onEnterAs !== undefined && member.status === 'active' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      icon={<LogIn size={15} />}
                      onSelect={() => {
                        onEnterAs(member);
                      }}
                    >
                      {t.team.enterAs}
                    </DropdownMenuItem>
                  </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem
                  tone="danger"
                  icon={<Trash2 size={15} />}
                  onSelect={() => {
                    act(usersRemoveMember(member.membership_id), t.team.removed);
                  }}
                >
                  {t.team.remove}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </section>
    </div>
  );
}

/** Роль, охват и ограничение — метками: их видно, не открывая меню. */
function GrantBadges({
  role,
  devOnly,
  siteName,
}: {
  role: string;
  devOnly: boolean;
  siteName?: string | null | undefined;
}) {
  return (
    <>
      <Badge size="sm" variant="outline">
        {t.team.roles[role] ?? role}
      </Badge>
      <Badge size="sm" variant="outline" mono>
        {siteName ?? t.team.wholeTenant}
      </Badge>
      {devOnly && (
        <Badge size="sm" tone="warning">
          {t.team.devOnlyBadge}
        </Badge>
      )}
    </>
  );
}

const WHOLE_TENANT = 'tenant';

function InviteForm({
  scope,
  onInvited,
}: {
  scope: PanelScope;
  onInvited: (token: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  // Охват: на экране сайта по умолчанию — этот сайт, у тенанта — весь тенант.
  const [target, setTarget] = useState(scope.kind === 'site' ? scope.siteID : WHOLE_TENANT);
  const [devOnly, setDevOnly] = useState(false);
  const [error, setError] = useState<string>();

  const invite = useMutation({
    mutationFn: async () => {
      const response = await usersInvite(scope.tenantID, {
        email: email.trim().toLowerCase(),
        role,
        dev_only: role === 'admin' ? false : devOnly,
        ...(target !== WHOLE_TENANT && { site_id: target }),
      });

      if (response.status !== 201) {
        throw new Error('Неожиданный ответ на приглашение');
      }

      return response.data.token;
    },
    onSuccess: (token) => {
      setEmail('');
      onInvited(token);
    },
    onError: (cause: unknown) => {
      setError(describe(cause));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);

    if (target === '') {
      setError(t.team.chooseSiteError);

      return;
    }

    invite.mutate();
  };

  return (
    <form className={styles['invite']} onSubmit={submit} noValidate>
      <div className={styles['invite__wide']}>
        <Field label={t.team.email} required error={error}>
          <Input
            type="email"
            placeholder="marina@example.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </Field>
      </div>

      <Field label={t.team.role} required>
        <Select
          value={role}
          onValueChange={(value) => {
            setRole(value as Role);
          }}
        >
          {ROLES.map((item) => (
            <SelectItem key={item} value={item} description={t.team.roleHints[item] ?? ''}>
              {t.team.roles[item]}
            </SelectItem>
          ))}
        </Select>
      </Field>

      <Field label={t.team.scope} required>
        <Select value={target} onValueChange={setTarget} placeholder={t.team.chooseSite}>
          <SelectItem value={WHOLE_TENANT}>{t.team.wholeTenant}</SelectItem>
          {scope.kind === 'site' ? (
            <SelectItem value={scope.siteID}>{t.team.thisSite}</SelectItem>
          ) : (
            scope.sites.map((site) => (
              <SelectItem key={site.id} value={site.id} description={site.slug}>
                {site.name}
              </SelectItem>
            ))
          )}
        </Select>
      </Field>

      <div className={styles['invite__actions']}>
        {/* У администратора ограничения не бывает: управлять людьми и не
            видеть Production — бессмыслица. Галочка тогда выключена. */}
        <Checkbox
          checked={role !== 'admin' && devOnly}
          disabled={role === 'admin'}
          onCheckedChange={(value) => {
            setDevOnly(value === true);
          }}
        >
          {role === 'admin' ? t.team.adminNoDevOnly : `${t.team.devOnly} — ${t.team.devOnlyHint}`}
        </Checkbox>

        <Button type="submit" iconLeft={<UserPlus size={15} />} loading={invite.isPending}>
          {t.team.invite}
        </Button>
      </div>
    </form>
  );
}

/**
 * Ссылка-приглашение показывается один раз.
 *
 * В базе лежит только её отпечаток, и восстановить ссылку нельзя даже нам.
 * Поэтому она подана заметно, с кнопкой копирования.
 */
function InviteLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className={styles['link']}>
      <p className={styles['link__note']}>{t.team.linkNote}</p>

      <div className={styles['link__row']}>
        <code className={styles['link__value']}>{link}</code>

        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          iconLeft={copied ? <Check size={14} /> : <Copy size={14} />}
          onClick={() => {
            navigator.clipboard
              .writeText(link)
              .then(() => {
                setCopied(true);
              })
              .catch(() => {
                toast.error(t.team.copyFailed, { description: t.team.copyFailedHint });
              });
          }}
        >
          {copied ? t.team.copied : t.team.copy}
        </Button>
      </div>
    </div>
  );
}

function describe(cause: unknown): string {
  return cause instanceof ApiError ? cause.detail : t.sites.unknownError;
}
